"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RequestService = void 0;
// eslint-disable-next-line max-classes-per-file
const ScopedBusinessEntity_1 = require("../ScopedBusinessEntity/ScopedBusinessEntity");
const SessionService_1 = require("../Session/SessionService");
const UnauthorizedError_1 = require("../Requests/UnauthorizedError");
const RequestError_1 = require("../Requests/RequestError");
class RequestService {
    baseUrl;
    defaultHeaders;
    sessionService = SessionService_1.sessionService;
    scopedBusinessEntity = new ScopedBusinessEntity_1.ScopedBusinessEntity();
    constructor({ baseUrl = null, defaultHeaders = { 'Content-Type': 'application/json' } } = {}) {
        this.baseUrl = baseUrl;
        this.defaultHeaders = defaultHeaders;
    }
    async get(url, options) {
        const response = await this.fetch('GET', url, options);
        return this.returnResponseBody(response);
    }
    async delete(url, options) {
        const response = await this.fetch('DELETE', url, options);
        return this.returnResponseBody(response);
    }
    async post(url, body, options) {
        const response = await this.fetch('POST', url, { ...options, body });
        return this.returnResponseBody(response);
    }
    async put(url, body, options) {
        const response = await this.fetch('PUT', url, { ...options, body });
        return this.returnResponseBody(response);
    }
    async patch(url, body, options) {
        const response = await this.fetch('PATCH', url, { ...options, body });
        return this.returnResponseBody(response);
    }
    // response.json() returns Promise<any>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async returnResponseBody(response) {
        // @ts-ignore: 'await' has no effect on the type of this expression
        const clonedResponse = await response.clone();
        try {
            // eslint-disable-next-line @typescript-eslint/return-await
            return await response.json();
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/return-await, no-return-await
            return await clonedResponse.text();
        }
    }
    /**
     * @throws RequestError
     * @return Promise<Response>
     */
    async fetch(method, url, { domain, resource, action, headers = {}, body = undefined, signal }) {
        await this.sessionService.ensureAuthorizedSession();
        const authorizationHeaders = await this.createAuthorizationHeaders({ domain, resource, action });
        const finalHeaders = this.mergeHeaders(authorizationHeaders, headers);
        const finalUrl = this.getUrl(url);
        const fetchOptions = this.mergeOptions(method, finalHeaders, body);
        const response = await fetch(finalUrl, {
            ...fetchOptions,
            signal,
        });
        const hasSucceeded = response.status >= 200 && response.status < 300;
        if (hasSucceeded) {
            return response;
        }
        const isUnauthorized = response.status === 401;
        if (isUnauthorized) {
            throw new UnauthorizedError_1.UnauthorizedError();
        }
        throw new RequestError_1.RequestError(await this.returnResponseBody(response));
    }
    mergeOptions(method, headers, body) {
        const fetchOptions = {
            method,
            headers,
            body: null,
        };
        if (method !== 'GET') {
            fetchOptions.body = JSON.stringify(body);
        }
        return fetchOptions;
    }
    mergeHeaders(authorizationHeaders, headers) {
        return {
            ...this.defaultHeaders,
            ...authorizationHeaders,
            ...headers,
        };
    }
    async createAuthorizationIdentityToken() {
        const sessionIdToken = await this.sessionService.getSessionToken();
        return `Bearer ${sessionIdToken}`;
    }
    createAuthorizationCacheKey({ domain, resource, action }) {
        const separator = ':';
        return [domain, resource, action].join(separator);
    }
    createAuthorizationScopedBusinessEntity() {
        const businessEntityIdKey = this.scopedBusinessEntity.getStorageKey();
        const businessEntityIdValue = this.scopedBusinessEntity.getId();
        if (!businessEntityIdValue) {
            return null;
        }
        return `${businessEntityIdKey}=${businessEntityIdValue}`;
    }
    async createAuthorizationHeaders({ domain, resource, action }) {
        const separator = ' ';
        const identityToken = await this.createAuthorizationIdentityToken();
        const cacheKey = this.createAuthorizationCacheKey({ domain, resource, action });
        const scopedBusinessEntity = this.createAuthorizationScopedBusinessEntity();
        return {
            Authorization: [identityToken, cacheKey, scopedBusinessEntity].join(separator).trimEnd(),
        };
    }
    getUrl(url) {
        if (this.baseUrl && !this.isUrl(url)) {
            return this.baseUrl + url;
        }
        return url;
    }
    isUrl(url) {
        return ['//', 'http://', 'https://'].some((protocol) => url.startsWith(protocol));
    }
}
exports.RequestService = RequestService;
//# sourceMappingURL=RequestService.js.map